import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { NzI18nService, NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { NoteService } from './features/manage-settings/notes/note.service';
import { AuthService } from './core/services/auth.service';
import {
  Observable,
  Subject,
  takeUntil,
  filter,
  distinctUntilChanged,
  map,
  combineLatest,
  debounceTime,
  share,
  switchMap,
  timer,
} from 'rxjs';
import { OrganizationDetailModel } from './features/organizations/organization.interface';
import { ReportLabelsService } from './features/manage-settings/report-labels/report-labels.service';
import { ReportLabel } from './features/manage-settings/report-labels/report-labels.model';
import { AppService } from './core/services/app.service';
import { BroadcastService } from './features/broadcast-config/broadcast.service';
import { Broadcast } from './features/broadcast-config/broadcast';
import { CommonModule } from '@angular/common';
import { BroadcastNotificationService } from './core/services/broadcast-notification.service';
import { CommonService } from './shared/services/common.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [
    { provide: NZ_I18N, useValue: en_US },
    BroadcastNotificationService,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'itb_frontend_angular';
  orgData: OrganizationDetailModel | null = null;
  reportLabels$: Observable<ReportLabel[]>;
  activeAlerts$: Observable<Broadcast[]>;
  private readonly USER_KEY = 'user_data';
  private readonly destroy$ = new Subject<void>();
  private routeChanged$ = new Subject<string>();
  private lastSignalRInitTime = 0;
  private readonly minSignalRInitInterval = 10000;
  private initialConnectionMade = false;
  isHomePage = false;

  constructor(
    private i18n: NzI18nService,
    private noteService: NoteService,
    private authService: AuthService,
    private reportLabelsService: ReportLabelsService,
    private appService: AppService,
    public router: Router,
    private broadcastService: BroadcastService,
    private broadcastNotificationService: BroadcastNotificationService,
    private commonService: CommonService
  ) {
    this.i18n.setLocale(en_US);
    this.isHomePage = this.router.url === '/';
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.isHomePage = event.urlAfterRedirects === '/';
      });
    this.reportLabels$ = this.reportLabelsService.reportLabels$;
    this.activeAlerts$ = this.broadcastService.alerts$;

    this.authService.authState$
      .pipe(
        takeUntil(this.destroy$),
        filter(state => state.isAuthenticated),
        switchMap(state => {
          return timer(1000).pipe(map(() => state));
        })
      )
      .subscribe(() => {
        const isExcluded = this.broadcastNotificationService.isExcludedRoute(
          this.router.url
        );
        if (!isExcluded) {
          this.safeInitializeSignalR();
        }
        const user = localStorage.getItem(this.USER_KEY);
        if (user) {
          const userData = this.authService.decryptData(user);
          this.commonService.getOrgList(userData);
        }
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
        debounceTime(1000)
      )
      .subscribe((event: NavigationEnd) => {
        const currentRoute = event.urlAfterRedirects;
        this.broadcastNotificationService.setCurrentRoute(currentRoute);
        this.routeChanged$.next(currentRoute);
      });

    this.setupSubscriptions();

    this.appService.networkStatus$
      .pipe(
        takeUntil(this.destroy$),
        filter(isOnline => isOnline),
        debounceTime(2000)
      )
      .subscribe(isOnline => {
        if (isOnline) {
          const isExcluded = this.broadcastNotificationService.isExcludedRoute(
            this.router.url
          );
          if (!isExcluded) {
            this.safeInitializeSignalR();
          }
        }
      });

    this.appService.notificationReceived$
      .pipe(
        takeUntil(this.destroy$),
        filter(notification => !!notification && !!notification.body),
        debounceTime(1000),
        filter(() => {
          const isExcluded = this.broadcastNotificationService.isExcludedRoute(
            this.router.url
          );
          return !isExcluded;
        })
      )
      .subscribe(notification => {
        if (notification.body) {
          this.broadcastNotificationService.handleNotification(
            notification.body
          );
        }
      });
  }

  private safeInitializeSignalR(): void {
    const now = Date.now();
    if (!this.initialConnectionMade) {
      this.initialConnectionMade = true;
      this.lastSignalRInitTime = now;
      this.appService.initializeSignalR();
      return;
    }

    if (now - this.lastSignalRInitTime < this.minSignalRInitInterval) {
      return;
    }

    this.lastSignalRInitTime = now;
    this.appService.initializeSignalR();
  }

  ngOnInit(): void {
    this.routeChanged$.next(this.router.url);
    this.appService.signalRConnected$
      .pipe(
        takeUntil(this.destroy$),
        filter(connected => connected)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.broadcastNotificationService.fetchBroadcastData(
            this.orgData?.organizationId || null
          );
        }, 1000);
      });
  }

  ngOnDestroy(): void {
    this.broadcastNotificationService.clearAllNotificationStates();
    this.destroy$.next();
    this.destroy$.complete();
    this.routeChanged$.complete();
    this.appService.disconnectSignalR();
  }

  private setupSubscriptions(): void {
    const validRoute$ = this.routeChanged$.pipe(
      distinctUntilChanged(),
      debounceTime(1000),
      map(url => ({
        url,
        isExcluded: this.broadcastNotificationService.isExcludedRoute(url),
      })),
      takeUntil(this.destroy$),
      share()
    );

    validRoute$.subscribe(({ isExcluded }) => {
      if (isExcluded) {
        this.appService.disconnectSignalR();
      } else {
        this.safeInitializeSignalR();

        this.broadcastNotificationService.setupBroadcastNotifications();
      }
    });

    combineLatest([
      validRoute$,
      this.authService.orgData$.pipe(
        distinctUntilChanged(
          (prev, curr) => prev?.organizationId === curr?.organizationId
        )
      ),
    ])
      .pipe(
        debounceTime(1000),
        filter(([routeInfo]) => !routeInfo.isExcluded),
        map(([, orgData]) => orgData),
        takeUntil(this.destroy$)
      )
      .subscribe(organization => {
        const orgId = organization?.organizationId || null;
        this.orgData = organization;

        this.broadcastNotificationService.setOrganizationId(orgId);

        if (organization?.organizationId) {
          this.loadOrganizationData(organization);
        }
      });
  }

  private loadOrganizationData(organization: OrganizationDetailModel): void {
    const orgId = organization.organizationId;

    if (!orgId) return;

    this.noteService.loadNotes({
      page: 1,
      pageSize: 5000,
      organizationId: orgId,
    });

    this.reportLabelsService.loadReportLabels(orgId);
    this.broadcastNotificationService.fetchBroadcastData(orgId);
  }

  private loadGlobalData(): void {
    this.broadcastNotificationService.fetchBroadcastData(null);
  }
}
